<template>
  <v-card height="100%" id="contractor-employees" tile>
    <v-card-title>
      <v-toolbar flat>
        <v-app-bar-nav-icon @click.prevent="$router.back()">
          <v-icon>chevron_left</v-icon>
        </v-app-bar-nav-icon>
        <v-toolbar-title>
          <v-row>
            <v-col class="col col-8">
              {{ resx.labels.employees }}
              <span v-if="contractorCompany && contractorCompany.contractor">{{
                `${contractorCompany.contractor.name}`
              }}</span>
            </v-col>
            <v-col class="col col-4">
              <v-text-field
                append-icon="search"
                :label="resx.labels.search"
                single-line
                hide-details
                v-model="filters.search"
              >
              </v-text-field>
            </v-col>
          </v-row>
        </v-toolbar-title>
      </v-toolbar>
    </v-card-title>
    <contractor-sections current="employees">
      <notes-panel
        v-if="isSystemUser && contractorCompany._id"
        :contractor-id="contractorCompany._id"
        scope="employees"
        :value="contractorCompany.notes ? contractorCompany.notes.employees : ''"
        :size="contractorCompany.notesSize ? contractorCompany.notesSize.employees : null"
      >
      </notes-panel>
    </contractor-sections>
    <v-card-text class="fixed-header">
      <v-row column fill-height>
        <v-col fill-height class="scrollable">
          <v-data-table
            :headers="headers"
            :items="items"
            :no-data-text="resx.messages.noEmployeesAvailable"
            :no-results-text="resx.messages.noEmployeesFound"
            :search="filters.search"
            sort-by="fullName"
            :items-per-page="itemsPerPage"
            :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
            :item-class="rowClass"
            @update:items-per-page="saveItemsPerPage"
          >
            <template v-slot:item.hasDelayed="{ item }">
              <td class="layout justify-center align-center px-0">
                <v-tooltip bottom v-if="item.isExcluded">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="grey">block</v-icon>
                  </template>
                  {{ resx.messages.excludedFromAptitude }}
                </v-tooltip>
                <v-tooltip bottom v-else-if="item.hasDelayed">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="error">error</v-icon>
                  </template>
                  {{ resx.messages.hasDelayedObligations }}
                </v-tooltip>
                <v-icon color="success" v-else>check_circle</v-icon>
              </td>
            </template>
            <template v-slot:item.name="{ item }">
              <td>{{ `${item.fullName}` }}</td>
            </template>
            <template v-slot:item.birthDate="{ item }">
              <td class="hidden-xs-only">
                {{ parseDate(item.birthDate) }}
              </td>
            </template>
            <template v-slot:item.identifier.number="{ item }">
              <td class="hidden-xs-only">
                {{
                  `${item.identifier.type.toUpperCase()}: ${
                    item.identifier.number
                  }`
                }}
              </td>
            </template>
            <template v-slot:item.type.name="{ item }">
              <td class="hidden-xs-only">{{ item.type.name }}</td>
            </template>
            <template v-slot:item.comments="{ item }">
              <td class="hidden-xs-only">{{ item.comments }}</td>
            </template>
            <template v-slot:item.actions="{ item }">
              <td class="justify-center layout px-0">
                <div class="d-flex align-center justify-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon @click="manageEmployeeObligation(item)">
                      <v-icon :color="'primary'">description</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ resx.labels.obligations }}</span>
                </v-tooltip>
                <v-menu content-class="contractor-item-menu" bottom auto>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEmployeeDialog(item)">
                      <v-list-item-action>
                        <v-icon color="primary">mode_edit</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>{{
                        resx.labels.edit
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="toggleExclusion(item)">
                      <v-list-item-action>
                        <v-icon v-if="item.isExcluded" color="primary">check_circle_outline</v-icon>
                        <v-icon v-else color="grey darken-1">block</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>{{
                        item.isExcluded ? resx.labels.enableEmployee : resx.labels.excludeEmployee
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="obligationExclusionsFor = item">
                      <v-list-item-action>
                        <v-icon color="grey darken-1">remove_circle_outline</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>Excluir obligaciones de contratista</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="remove(item)">
                      <v-list-item-action>
                        <v-icon color="error">delete</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>{{
                        resx.labels.delete
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <div>
          <v-btn
            color="accent"
            class="addButton"
            fab
            bottom
            right
            absolute
            @click="openEmployeeDialog()"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </div>
      </v-row>
      <v-row>
        <edit-contractor-employees
          v-model="openDialog"
          v-on:close="onClose"
          v-on:save="onSave"
          :contractorId="contractorCompany._id"
        ></edit-contractor-employees>
      </v-row>
      <employee-obligation-exclusions-dialog
        v-if="obligationExclusionsFor"
        :employee="obligationExclusionsFor"
        :contractor-company-id="$route.params.id"
        @close="obligationExclusionsFor = null"
      />
    </v-card-text>
  </v-card>
</template>

<script>
"use strict";

import { sprintf } from "sprintf-js";
import roles from "@/utils/roles";
import moment from "moment";

import contractorCompanyService from "@/services/ContractorCompanyService";
import store from "@/store/store";

import editContractorEmployees from "./edit-contractor-employees.view.vue";
import employeeObligationExclusionsDialog from "./employee-obligation-exclusions-dialog.component.vue";
import notesPanel from "../notes-panel.vue";
import contractorSections from "../contractor-sections.component.vue";

export default {
  async mounted() {
    this.contractorCompany = await contractorCompanyService.getById(
      this.$route.params.id
    );
    this.setItems(
      await contractorCompanyService.listEmployee(this.$route.params.id)
    );
  },
  components: {
    editContractorEmployees,
    employeeObligationExclusionsDialog,
    notesPanel,
    contractorSections
  },
  data() {
    const resx = this.$store.state.locale.resx;
    return {
      itemsPerPage: parseInt(localStorage.getItem(this.$options.name + '_itemsPerPage')) || 50,
      headers: [
        {
          align: "center",
          sortable: false,
          width: 48,
          value: "hasDelayed"
        },
        {
          text: resx.labels.name,
          value: "fullName"
        },
        {
          class: "hidden-xs-only",
          text: resx.labels.birthDate,
          value: "birthDate"
        },
        {
          class: "hidden-xs-only",
          text: resx.labels.identifierText,
          value: "identifier.number"
        },
        {
          class: "hidden-xs-only",
          text: resx.labels.contractorEmployeeType,
          value: "type.name"
        },
        {
          class: "hidden-xs-only",
          text: resx.labels.comments,
          value: "comments"
        },
        {
          text: resx.labels.actions,
          align: "center",
          sortable: false,
          value: "actions"
        },
        // this 2 below are used for filtering only
        {
          align: " d-none",
          value: "strippedName"
        },
        {
            align: " d-none",
          value: "strippedIdentifier"
        },
        {
            align: " d-none",
            value: "strippedTypeName"
        },
        {
          align: " d-none",
          value: "lastName"
        },
        {
          align: " d-none",
          value: "firstName"
        }
      ],
      items: [],
      filters: {
        search: null
      },
      openDialog: null,
      obligationExclusionsFor: null,
      contractorCompany: {}
    };
  },
  computed: {
    // Las notas son del personal del sistema.
    isSystemUser() {
      return roles.loggedUserHasRoles(
        this.$store.state.auth.user.roles,
        this.$store.state.constants.roles.ALL_SYS_ROLES
      );
    }
  },
  methods: {
    saveItemsPerPage(val) {
      this.itemsPerPage = val;
      localStorage.setItem(this.$options.name + '_itemsPerPage', val);
    },
    // Toda lista que vuelve del servidor pasa por aca: los campos derivados
    // (fullName y los stripped* del buscador) se calculan en un solo lugar.
    setItems(employees) {
      employees.forEach(function(employee) {
        employee.fullName = employee.lastName + ", " + employee.firstName;
        employee.strippedIdentifier = employee.identifier.number.replaceAll(" ", "").replaceAll("-", "");
        employee.strippedName = employee.fullName.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "").replaceAll(" ", "").replaceAll("-", "");
        employee.strippedTypeName = employee.type.name.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "").replaceAll(" ", "").replaceAll("-", "");
      });
      this.items = employees;
    },
    rowClass(item) {
      return item.isExcluded ? "excluded-row" : "";
    },
    // Excluir / habilitar para el analisis de aptitud. Mientras esta excluido,
    // la fila se ve gris y sus obligaciones no cuentan; se puede volver a
    // habilitar desde este mismo menu.
    async toggleExclusion(employee) {
      const employees = await contractorCompanyService.setEmployeeExclusion(
        this.contractorCompany._id,
        employee._id,
        !employee.isExcluded
      );
      if (employees) {
        this.setItems(employees);
      }
    },
    async remove(employee) {
      await this.botConfirmDialog({
        title: `${
          this.resx.labels.delete
        } ${this.resx.labels.employee.toLowerCase()}`,
        content: sprintf(
          this.resx.messages.deleteEmployeeMessage,
          `${employee.firstName} ${employee.lastName}`
        )
      });
      const employees = await contractorCompanyService.deleteEmployee(
        this.contractorCompany._id,
        employee._id
      );
      if (employees) {
        this.setItems(employees);
      }
    },
    onClose() {
      this.openDialog = null;
    },
    async onSave(action) {
      let employees = new Array();
      if (action.isNew) {
        employees = await contractorCompanyService.createEmployee(
          this.contractorCompany._id,
          action.item
        );
      } else {
        employees = await contractorCompanyService.editEmployee(
          this.contractorCompany._id,
          action.item
        );
      }
      if (employees.length > 0) {
        this.setItems(employees);
      }
      this.onClose();
    },
    parseDate(d) {
      return moment(d).format(store.state.constants.DATE_FORMAT);
    },
    openEmployeeDialog(employee) {
      let selectedItem = employee ? employee : {};
      selectedItem.contractorId = this.contractorCompany._id;
      this.openDialog = { selectedItem };
    },
    manageEmployeeObligation(employee) {
      this.$router.push(
        `/contractors/${this.contractorCompany._id}/employees/${employee._id}/obligations`
      );
    },
    nameFilter(value) {
      // If this filter has no value we just skip the entire filter.
      if (!this.filters.search) {
        return true;
      }
      // Check if the current loop value (The dessert name)
      // partially contains the searched word.
      return value.toLowerCase().includes(this.filters.search.toLowerCase());
    }
  },
  mixins: []
};
</script>

<style lang="scss">
#contractor-employees {
  .icon {
    cursor: pointer;
  }

  // Personal excluido del analisis de aptitud: la fila queda gris pero el
  // menu de los tres puntos sigue activo para volver a habilitarlo.
  tr.excluded-row {
    background-color: rgba(0, 0, 0, 0.04);
    td {
      color: rgba(0, 0, 0, 0.38);
    }
  }

  .theme--light.v-data-table {
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
  }

  .v-toolbar__title {
    width: 100%;
  }
}
</style>
